import {
  collection,
  CollectionReference,
  doc,
  DocumentReference,
  Firestore,
  query,
  where,
} from 'firebase/firestore';
import { isEmpty } from 'lodash';
import isEqual from 'lodash/isEqual';
import { collectionData, docData } from 'rxfire/firestore';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { getPredefinedDateRange } from '../../../../utils/date';
import {
  IRange,
  ISelectableMetric,
  ISelectableTag,
  ISelectableTestType,
  IUser,
} from '../../../types';
import { createDefaultTemplates } from '../../../writers/settings/reports/ratioReportSettings';
import { authenticatedUserProvider } from '../../userProvider';
import { ISettingsTemplate } from './types';

export interface IRatioReportSettingEntity extends IRatioReportSettingOptions {
  selected: ISettingsTemplate<IRatioReportSettings>;
}

export interface IRatioReportSettingOptions {
  timeframe: IRange;
  filter: boolean;
  dsi?: ISpecialReportOptions;
  eur?: ISpecialReportOptions;
}

export interface ISpecialReportOptions {
  metricOneTags?: ISelectableTag[];
  metricTwoTags?: ISelectableTag[];
}

export interface IRatioReportSettings {
  metricOne: {
    testType: ISelectableTestType;
    tags: ISelectableTag[];
    metric: ISelectableMetric;
  };
  metricTwo: {
    testType: ISelectableTestType;
    tags: ISelectableTag[];
    metric: ISelectableMetric;
  };
}

export interface IRatioReportSettingsState extends IRatioReportSettingOptions {
  selected: ISettingsTemplate<IRatioReportSettings>;
  templates: Array<ISettingsTemplate<IRatioReportSettings>>;
}

const defaultTimeframe = getPredefinedDateRange('year');

/**
 * @description observes the ratio report settings
 */
export const ratioReportSettingsProvider =
  (): Observable<IRatioReportSettingsState> => {
    return authenticatedUserProvider().pipe(
      switchMap(
        (
          user: IUser
        ): Observable<
          [
            ISettingsTemplate<IRatioReportSettings>,
            IRatioReportSettingOptions,
            Array<ISettingsTemplate<IRatioReportSettings>>
          ]
        > => {
          const listenerArgs = {
            userId: user.id as string,
            firestore: user.services.firestore,
          };
          return combineLatest([
            selectedTemplateProvider(listenerArgs),
            ratioReportSettingsOptionsProvider(listenerArgs),
            templatesProvider(listenerArgs),
          ]);
        }
      ),
      map(
        (
          result: [
            ISettingsTemplate<IRatioReportSettings>,
            IRatioReportSettingOptions,
            Array<ISettingsTemplate<IRatioReportSettings>>
          ]
        ): IRatioReportSettingsState => {
          const [selected, templateAgnosticOptions, templates] = result;
          let defaultSelected: ISettingsTemplate<IRatioReportSettings>;

          if (isEmpty(selected)) {
            defaultSelected = templates[0];
          }

          return {
            selected: selected ?? defaultSelected,
            templates,
            timeframe: templateAgnosticOptions?.timeframe
              ? templateAgnosticOptions?.timeframe
              : defaultTimeframe,
            filter: templateAgnosticOptions?.filter
              ? templateAgnosticOptions?.filter
              : false,
            eur: templateAgnosticOptions?.eur
              ? templateAgnosticOptions.eur
              : null,
            dsi: templateAgnosticOptions?.dsi
              ? templateAgnosticOptions.dsi
              : null,
          };
        }
      )
    );
  };

interface IListenerArgs {
  userId: string;
  firestore: Firestore;
}

/**
 * @description observes the settings templates the user has saved
 * @param userId
 * @returns
 */
const templatesProvider = (
  args: IListenerArgs
): Observable<Array<ISettingsTemplate<IRatioReportSettings>>> => {
  const { userId, firestore } = args;
  return collectionData(
    query(
      collection(
        firestore,
        `userProfiles/${userId}/settings/ratioReport/templates`
      ) as CollectionReference<ISettingsTemplate<IRatioReportSettings>>,
      where('active', '==', true)
    )
  ).pipe(
    map(
      (
        tmpls: Array<ISettingsTemplate<IRatioReportSettings>>
      ): Array<ISettingsTemplate<IRatioReportSettings>> => {
        if (tmpls.length === 0) {
          createDefaultTemplates();
        }
        return tmpls;
      }
    ),
    filter(
      (tmpls: Array<ISettingsTemplate<IRatioReportSettings>>): boolean =>
        tmpls.length > 0
    )
  );
};

/**
 * @description observes changes the subset of the ratio settings that are not template specific
 * @param userId
 * @returns
 */
const ratioReportSettingsOptionsProvider = (
  args: IListenerArgs
): Observable<IRatioReportSettingOptions> => {
  const { userId, firestore } = args;
  return docData(
    doc(
      firestore,
      `userProfiles/${userId}/settings/ratioReport`
    ) as DocumentReference<IRatioReportSettingEntity>
  ).pipe(
    map(
      (entity: IRatioReportSettingEntity): IRatioReportSettingOptions => ({
        timeframe: entity?.timeframe,
        filter: entity?.filter,
        eur: entity?.eur,
        dsi: entity?.dsi,
      })
    ),
    distinctUntilChanged(
      (
        prev: IRatioReportSettingOptions,
        curr: IRatioReportSettingOptions
      ): boolean => isEqual(prev, curr)
    )
  );
};

/**
 * @description observes the selected template
 * @param userId
 * @returns
 */
const selectedTemplateProvider = (
  args: IListenerArgs
): Observable<ISettingsTemplate<IRatioReportSettings>> => {
  const { userId, firestore } = args;
  return docData(
    doc(
      firestore,
      `userProfiles/${userId}/settings/ratioReport`
    ) as DocumentReference<IRatioReportSettingEntity>
  ).pipe(
    map(
      (
        entity: IRatioReportSettingEntity
      ): ISettingsTemplate<IRatioReportSettings> => entity?.selected
    ),
    distinctUntilChanged(
      (
        prev: ISettingsTemplate<IRatioReportSettings>,
        curr: ISettingsTemplate<IRatioReportSettings>
      ): boolean => isEqual(prev, curr)
    )
  );
};
